import { useState } from "react";
import { Copy, CreditCard, Smartphone, Building2, AlertCircle, CheckCircle2, ScanLine, Upload, Send } from "lucide-react";
import { toast } from "sonner";
import PageHeader from "../components/PageHeader";
import SEO from "../components/SEO";
import { SOCIAL } from "../data/siteData";
import ParentPolicyClauses from "../components/ParentPolicyClauses";
import PaymentConsentGate from "../components/PaymentConsentGate";
import PaymentScreenshotUpload from "../components/PaymentScreenshotUpload";
import { PAYMENT_INFO, HEAD_OFFICE } from "../data/siteData";

const UPI_QR_IMAGE = "/assets/anand-tutorials-upi-qr.jpg";

export default function PayNow() {
    const [consent, setConsent] = useState(null);

    const copy = (val, label) => {
        navigator.clipboard.writeText(val);
        toast.success(`${label} copied`);
    };

    return (
        <div data-testid="pay-now-page">
            <SEO
                title="Pay Now — Anand Tutorials Fee Payment Details"
                description="Anand Tutorials secure payment details. UPI, bank account, IFSC, PhonePe / GPay / Paytm. Full fee, cancellation and refund policy."
                keywords="pay tuition fees, Anand Tutorials payment, UPI payment, fee policy"
                path="/pay-now"
                noindex
            />
            <PageHeader
                eyebrow="Payment & Policies"
                title="Pay Now — Anand Tutorials"
                subtitle="Thank you for choosing Anand Tutorials. Please review the 9 Parent Terms and confirm your agreement before the payment details are revealed."
            />

            {/* Terms — shown first */}
            <section className="py-16 bg-[#F8FAFC]">
                <div className="max-w-4xl mx-auto px-6 lg:px-8">
                    <div className="text-xs font-semibold uppercase tracking-[0.3em] text-[#3B82F6] mb-3">Terms & Conditions</div>
                    <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-3">Anand Tutorials — Parent Terms</h2>
                    <p className="text-sm text-slate-500 mb-8">The 9 key terms every parent must acknowledge before payment. Full legally-binding version at <a href="/policies/parent" className="text-[#1E3A8A] font-semibold underline">/policies/parent</a>.</p>

                    <ParentPolicyClauses />

                    {/* Fee & refund structure (retained for reference) */}
                    <div className="mt-12">
                        <h3 className="font-heading text-xl font-bold mb-4">Fee Cycle & Refund Structure</h3>
                        <div className="space-y-4">
                            {[
                                ["A. After Demo Class", "To continue with the same tutor, the monthly fee must be paid in advance."],
                                ["B. Monthly Fee Cycle", "The next month's fee should be paid within 3 days of plan completion. Classes will be paused in case of delayed payment."],
                                ["C. Tutor Change Policy", "If a tutor change is requested, completed sessions with the current tutor will be counted. New tutor sessions will begin as a fresh cycle."],
                                ["D. Class Discontinuation", "If classes are discontinued during an active plan, any eligible refund will be calculated based on remaining sessions only."],
                                ["E. Refund Structure", "20-session plan → refund on unused sessions · 12-session plan → refund on unused sessions · (applicable similarly for all plans)."],
                            ].map(([t, d]) => (
                                <div key={t} className="bg-white border border-slate-200 rounded-xl p-5">
                                    <h4 className="font-heading font-semibold text-[#1E3A8A] mb-1.5">{t}</h4>
                                    <p className="text-sm text-slate-600 leading-relaxed">{d}</p>
                                </div>
                            ))}
                        </div>
                    </div>

                    <p className="mt-8 text-sm text-slate-500 italic">Your cooperation helps us maintain quality and consistency in your child's learning experience. — Team Anand Tutorials</p>
                </div>
            </section>

            {/* Consent gate — must be accepted before payment details reveal */}
            {!consent && <PaymentConsentGate onAccepted={setConsent} />}

            {/* Payment details — only shown AFTER consent */}
            {consent && (
                <section className="py-16 bg-white" data-testid="payment-details-section">
                    <div className="max-w-5xl mx-auto px-6 lg:px-8 mb-8">
                        <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-5 flex items-start gap-3">
                            <CheckCircle2 className="w-6 h-6 text-emerald-600 shrink-0 mt-0.5" />
                            <div>
                                <h4 className="font-heading font-semibold text-[#0F172A] mb-0.5">Consent recorded. Thank you, {consent.name}.</h4>
                                <p className="text-sm text-emerald-800">
                                    Reference: <code className="font-mono text-xs bg-white px-1.5 py-0.5 rounded border border-emerald-200">{consent.id.slice(0, 8).toUpperCase()}</code> · Accepted at {new Date(consent.accepted_at).toLocaleString()}.
                                    Please include this reference when sharing your payment screenshot on WhatsApp.
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="max-w-5xl mx-auto px-6 lg:px-8 mb-8">
                        {/* V99 — Razorpay Payment Link. Additive; existing QR / UPI / Bank flows
                            below and screenshot-upload form after are 100% untouched. Opens the
                            Razorpay-hosted checkout in a new tab (parents then still upload the
                            screenshot below so the verification workflow is unchanged). */}
                        <div
                            data-testid="razorpay-card"
                            className="group relative overflow-hidden rounded-2xl bg-gradient-to-br from-white via-blue-50/40 to-indigo-50/60 border border-blue-100 shadow-md hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300 p-6 sm:p-8"
                        >
                            <div className="pointer-events-none absolute -top-16 -right-16 w-56 h-56 rounded-full bg-blue-200/30 blur-3xl group-hover:bg-blue-300/40 transition-colors" />
                            <div className="relative flex flex-col md:flex-row md:items-center gap-6">
                                <div className="flex-1">
                                    <div className="inline-flex items-center gap-2 bg-white text-[#1E3A8A] text-[11px] font-bold uppercase tracking-[0.18em] px-2.5 py-1 rounded border border-blue-100 shadow-sm">
                                        💳 Pay Securely via Razorpay
                                    </div>
                                    <h3 className="font-heading text-xl sm:text-2xl font-bold mt-3 text-[#0F172A]">
                                        Pay Online via Razorpay
                                    </h3>
                                    <p className="text-slate-600 text-sm mt-2 max-w-xl">
                                        Pay securely using UPI, Credit Card, Debit Card, Net Banking, Wallets and more — one gateway, every option.
                                    </p>
                                    <div className="mt-4 flex flex-wrap gap-1.5">
                                        {["UPI", "Credit Card", "Debit Card", "Net Banking", "Wallets"].map((m) => (
                                            <span key={m} className="text-[10px] font-semibold tracking-wide uppercase px-2 py-0.5 rounded-full bg-white text-[#1E3A8A] border border-blue-100">{m}</span>
                                        ))}
                                    </div>
                                </div>
                                <div className="flex flex-col items-stretch md:items-end md:min-w-[260px]">
                                    <a
                                        href="https://razorpay.me/@anandtutorials"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        data-testid="razorpay-pay-btn"
                                        className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-gradient-to-r from-[#1E3A8A] to-[#2563EB] text-white font-semibold hover:from-[#1E40AF] hover:to-[#1D4ED8] active:scale-[0.98] transition-all shadow-lg shadow-blue-600/25"
                                    >
                                        <span className="text-lg leading-none">💳</span> Pay via Razorpay
                                        <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 opacity-90"><path d="M11 3a1 1 0 100 2h2.586L8.293 10.293a1 1 0 001.414 1.414L15 6.414V9a1 1 0 102 0V4a1 1 0 00-1-1h-5z"/><path d="M5 5a2 2 0 00-2 2v8a2 2 0 002 2h8a2 2 0 002-2v-3a1 1 0 10-2 0v3H5V7h3a1 1 0 000-2H5z"/></svg>
                                    </a>
                                    <p className="mt-2 text-[10.5px] text-slate-500 md:text-right leading-relaxed">
                                        🔒 Secure SSL Encrypted Payment<br />
                                        Powered by Razorpay · Instant Payment Confirmation
                                    </p>
                                </div>
                            </div>
                            <div className="relative mt-6 border-t border-blue-100 pt-4 flex items-start gap-2 text-[12px] text-slate-600">
                                <span className="text-amber-500 shrink-0">ℹ️</span>
                                <p>
                                    <strong className="text-slate-800">After payment</strong>, please still upload your payment screenshot in the form below so we can attach it to your child&apos;s file and confirm your payment in the admin dashboard.
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="max-w-5xl mx-auto px-6 lg:px-8 grid lg:grid-cols-2 gap-8">
                        {/* QR — primary fast-pay path */}
                        <div className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-8 shadow-md flex flex-col items-center" data-testid="payment-qr-card">
                            <div className="w-full text-left mb-4">
                                <div className="inline-flex items-center gap-2 bg-blue-50 text-[#1E3A8A] text-[11px] font-bold uppercase tracking-[0.18em] px-2 py-1 rounded">
                                    <ScanLine className="w-3.5 h-3.5" /> Scan to Pay
                                </div>
                                <h3 className="font-heading text-xl sm:text-2xl font-bold mt-2">
                                    Official Anand Tutorials UPI QR
                                </h3>
                                <p className="text-slate-500 text-sm mt-1">
                                    Open any UPI app (GPay / PhonePe / Paytm / BHIM) and scan to pay <strong>ANAND HOME TUTORIALS</strong>.
                                </p>
                            </div>
                            <div className="w-full max-w-sm rounded-xl overflow-hidden border border-slate-200 bg-white">
                                <img
                                    src={UPI_QR_IMAGE}
                                    alt="Anand Tutorials UPI payment QR code — ICICI Bank, ANAND HOME TUTORIALS, +91 7000530316"
                                    className="w-full h-auto block"
                                    width={1080}
                                    height={1107}
                                    loading="eager"
                                    decoding="async"
                                    data-testid="payment-qr-image"
                                />
                            </div>
                            <div className="w-full mt-4 grid grid-cols-2 gap-3 text-xs">
                                <div className="bg-slate-50 rounded-lg p-3">
                                    <div className="text-slate-400 uppercase tracking-wider text-[10px] mb-0.5">Merchant</div>
                                    <div className="font-semibold text-[#0F172A]">ANAND HOME TUTORIALS</div>
                                </div>
                                <div className="bg-slate-50 rounded-lg p-3">
                                    <div className="text-slate-400 uppercase tracking-wider text-[10px] mb-0.5">Support</div>
                                    <div className="font-semibold text-[#0F172A]">Call &amp; WhatsApp</div>
                                </div>
                            </div>
                        </div>

                        {/* UPI ID + Bank */}
                        <div className="space-y-6">
                            <div className="bg-gradient-to-br from-[#1E3A8A] to-[#1E40AF] text-white rounded-2xl p-6 sm:p-8 shadow-xl">
                                <Smartphone className="w-8 h-8 text-amber-400 mb-3" />
                                <h3 className="font-heading text-xl sm:text-2xl font-bold mb-1">UPI ID / Phone</h3>
                                <p className="text-blue-200 text-sm mb-4">If you can&apos;t scan, pay directly to:</p>
                                <Row label="UPI ID" value={PAYMENT_INFO.upi} onCopy={() => copy(PAYMENT_INFO.upi, "UPI ID")} dark />
                                <Row label="UPI Phone Number" value={PAYMENT_INFO.upiPhone} onCopy={() => copy(PAYMENT_INFO.upiPhone, "Phone")} dark />
                            </div>
                            <div className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-8 shadow-md">
                                <Building2 className="w-8 h-8 text-[#1E3A8A] mb-3" />
                                <h3 className="font-heading text-xl sm:text-2xl font-bold mb-1">Bank Transfer (NEFT / IMPS / RTGS)</h3>
                                <p className="text-slate-500 text-sm mb-4">For larger amounts.</p>
                                <Row label="Account Holder" value={PAYMENT_INFO.accountHolder} onCopy={() => copy(PAYMENT_INFO.accountHolder, "Account holder")} />
                                <Row label="Account Number" value={PAYMENT_INFO.accountNumber} onCopy={() => copy(PAYMENT_INFO.accountNumber, "Account number")} />
                                <Row label="IFSC Code" value={PAYMENT_INFO.ifsc} onCopy={() => copy(PAYMENT_INFO.ifsc, "IFSC")} />
                                <Row label="Bank" value="ICICI Bank" />
                            </div>
                        </div>
                    </div>

                    {/* 4-step flow */}
                    <div className="max-w-5xl mx-auto px-6 lg:px-8 mt-12">
                        <div className="text-xs font-semibold uppercase tracking-[0.3em] text-[#3B82F6] mb-3">
                            How it works
                        </div>
                        <h3 className="font-heading text-2xl sm:text-3xl font-bold mb-6">
                            Four steps to complete your payment
                        </h3>
                        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                            {[
                                { i: ScanLine, t: "1. Scan the QR Code", d: "Open any UPI app on your phone and scan the QR above." },
                                { i: CreditCard, t: "2. Complete the Payment", d: "Enter the agreed amount and confirm in your UPI app." },
                                { i: Upload, t: "3. Upload Screenshot", d: "Attach the payment confirmation from your UPI app below." },
                                { i: Send, t: "4. Submit Details", d: "Hit submit. We verify within one working day and confirm on WhatsApp." },
                            ].map(({ i: Icon, t, d }) => (
                                <div key={t} className="bg-white border border-slate-200 rounded-2xl p-5 hover:shadow-md transition-shadow">
                                    <div className="w-10 h-10 rounded-xl bg-blue-50 text-[#1E3A8A] flex items-center justify-center mb-3">
                                        <Icon className="w-5 h-5" />
                                    </div>
                                    <h4 className="font-heading font-bold text-[#0F172A] mb-1.5 text-sm">{t}</h4>
                                    <p className="text-xs text-slate-600 leading-relaxed">{d}</p>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Screenshot upload form */}
                    <div className="max-w-3xl mx-auto px-6 lg:px-8 mt-10" data-testid="payment-upload-section">
                        <div className="mb-5">
                            <div className="text-xs font-semibold uppercase tracking-[0.3em] text-[#3B82F6] mb-2">
                                Step 3 + 4 — Upload &amp; Submit
                            </div>
                            <h3 className="font-heading text-2xl sm:text-3xl font-bold">
                                Upload your payment screenshot
                            </h3>
                            <p className="text-sm text-slate-500 mt-1">
                                Reference <code className="font-mono text-xs bg-slate-100 px-1.5 py-0.5 rounded">{consent.id.slice(0, 8).toUpperCase()}</code> is auto-filled below. Just attach your screenshot and submit.
                            </p>
                        </div>
                        <PaymentScreenshotUpload
                            prefill={{
                                name: consent.name,
                                phone: consent.phone,
                                reference_short: consent.id.slice(0, 8).toUpperCase(),
                            }}
                        />
                    </div>

                    <div className="max-w-3xl mx-auto px-6 lg:px-8 mt-8">
                        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5 flex gap-3">
                            <AlertCircle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                            <p className="text-xs text-slate-700">
                                Trouble uploading? You can also send the screenshot to{" "}
                                <a className="text-[#1E3A8A] font-semibold" href={`https://wa.me/${SOCIAL.whatsapp}`}>WhatsApp Us</a>{" "}
                                or email{" "}
                                <a className="text-[#1E3A8A] font-semibold" href={`mailto:${HEAD_OFFICE.email}`} data-testid="paynow-email-primary">{HEAD_OFFICE.email}</a>
                                {" "}or{" "}
                                <a className="text-[#1E3A8A] font-semibold" href="mailto:info@anandtutorials.com" data-testid="paynow-email-business">info@anandtutorials.com</a>
                                {" "}— our team will confirm receipt the same day.
                            </p>
                        </div>
                    </div>
                </section>
            )}
        </div>
    );
}

const Row = ({ label, value, onCopy, dark }) => (
    <div className={`flex items-center justify-between py-3 ${dark ? "border-b border-white/10" : "border-b border-slate-100"} last:border-0`}>
        <div>
            <div className={`text-xs uppercase tracking-[0.15em] ${dark ? "text-blue-200" : "text-slate-400"}`}>{label}</div>
            <div className={`font-heading font-semibold ${dark ? "text-white" : "text-[#0F172A]"} text-base sm:text-lg break-all`}>{value}</div>
        </div>
        {onCopy && (
            <button onClick={onCopy} className={`shrink-0 ml-4 p-2 rounded-lg ${dark ? "bg-white/10 hover:bg-white/20" : "bg-slate-100 hover:bg-slate-200"} transition-colors`} aria-label="Copy" data-testid={`copy-${label.toLowerCase().replace(/\s+/g, "-")}`}>
                <Copy className={`w-4 h-4 ${dark ? "text-white" : "text-slate-600"}`} />
            </button>
        )}
    </div>
);
